import type { ParamValues, PatternLayer, ReliefFinish } from '../types';
import { clamp, clamp01 } from '../math/scalar';
import { hashString } from '../math/random';
import { createLayer } from '../design/defaults';
import type { TextureGenerationResult } from './provider';

/**
 * The offline prompt synthesiser.
 *
 * It reads a prompt and assembles a layer stack from the registered patterns.
 * It is a heuristic, not a model — and that is a deliberate trade: it runs
 * instantly, needs no key, sends nothing anywhere, and always produces a
 * parametric design the user can keep editing. A generated image would give a
 * fixed bitmap; this gives sliders.
 *
 * Every rule below is plain data, so extending the vocabulary is a matter of
 * adding keywords rather than changing code.
 */

interface PromptContext {
  prompt: string;
  words: Set<string>;
  seed: number;
  /** Multiplier on feature size; `< 1` means finer. */
  scale: number;
  /** Multiplier on relief depth. */
  depth: number;
  /** `-1` fully smooth, `+1` fully faceted. */
  sharpness: number;
  angle: number;
  /** Explicit "across N panels" request, if the prompt names one. */
  span: number | null;
  irregular: boolean;
  terraced: boolean;
  faded: boolean;
  notes: string[];
}

interface MotifResult {
  patternId: string;
  params: ParamValues;
}

interface MotifRule {
  id: string;
  /**
   * Words that name this motif and little else. They score double, so
   * "cracked desert earth" resolves to cracked cells rather than to dunes:
   * "cracked" describes the texture, "desert" only the setting.
   */
  strong: readonly string[];
  /** Supporting words that nudge a rule without deciding it on their own. */
  keywords: readonly string[];
  depth: number;
  build(context: PromptContext): MotifResult;
}

const MODIFIERS = {
  deeper: ['deep', 'dramatic', 'bold', 'pronounced', 'strong', 'heavy', 'extreme', 'sculptural'],
  shallower: ['subtle', 'shallow', 'gentle', 'minimal', 'faint', 'delicate', 'slight', 'quiet'],
  finer: ['fine', 'small', 'tight', 'dense', 'micro', 'intricate', 'detailed', 'narrow'],
  coarser: ['large', 'big', 'coarse', 'chunky', 'wide', 'broad', 'massive', 'oversized'],
  sharper: ['sharp', 'crisp', 'angular', 'faceted', 'hard', 'geometric', 'precise', 'edgy'],
  softer: ['smooth', 'soft', 'round', 'rounded', 'flowing', 'organic', 'fluid', 'silky'],
  vertical: ['vertical', 'upright', 'column', 'columns', 'standing'],
  horizontal: ['horizontal', 'across', 'lying', 'landscape'],
  diagonal: ['diagonal', 'slanted', 'angled', 'oblique'],
  irregular: [
    'random',
    'irregular',
    'natural',
    'weathered',
    'rough',
    'noisy',
    'imperfect',
    'eroded',
  ],
  terraced: [
    'terraced',
    'stepped',
    'contour',
    'contours',
    'layered',
    'topographic',
    'stratified',
    'steps',
  ],
  faded: ['fade', 'faded', 'fading', 'gradient', 'ombre', 'dissolve', 'graduated'],
} as const;

/**
 * Motif rules, most specific first.
 *
 * Order matters on a tie: "ocean waves" should land on waves rather than on the
 * generic organic fallback, so the specific rules come first.
 */
const MOTIF_RULES: readonly MotifRule[] = [
  {
    id: 'chevron',
    strong: ['chevron', 'herringbone', 'zigzag'],
    keywords: ['arrow', 'arrows', 'v-shape'],
    depth: 10,
    build: (context) => ({
      patternId: 'chevron',
      params: {
        profile: context.sharpness >= 0 ? 'triangle' : 'sine',
        wavelength: span(context, 0.5),
        zigSpan: span(context, 1),
        amplitude: 1,
        angle: context.angle,
      },
    }),
  },
  {
    id: 'ripple',
    strong: ['ripple', 'ripples', 'concentric'],
    keywords: ['rings', 'pond', 'target', 'radar', 'sonar'],
    depth: 7,
    build: (context) => ({
      patternId: 'ripple',
      params: {
        profile: context.sharpness >= 0.5 ? 'triangle' : 'sine',
        wavelength: span(context, 0.4),
        centerX: 0.5,
        centerY: 0.5,
        falloff: 0.25,
      },
    }),
  },
  {
    id: 'starburst',
    strong: ['starburst', 'sunburst', 'spiral'],
    keywords: ['star', 'burst', 'radial', 'sunrise', 'rays'],
    depth: 9,
    build: (context) => ({
      patternId: 'starburst',
      params: {
        arms: Math.round(clamp(16 / context.scale, 4, 48)),
        twist: context.words.has('spiral') ? 0.8 : 0.2,
        taper: 0.4,
        profile: context.sharpness >= 0 ? 'triangle' : 'sine',
      },
    }),
  },
  {
    id: 'waves',
    strong: ['wave', 'waves', 'swell', 'wavy'],
    keywords: ['ocean', 'sea', 'water', 'surf', 'tide'],
    depth: 9,
    build: (context): MotifResult => {
      // "Ocean" implies something that never repeats exactly, so an irregular
      // prompt gets the noise-driven dunes instead of a metronomic sine.
      if (context.irregular || context.words.has('ocean') || context.words.has('sea')) {
        return {
          patternId: 'dunes',
          params: {
            profile: context.sharpness >= 0.5 ? 'triangle' : 'sine',
            wavelength: span(context, 1.25),
            turbulence: 0.5,
            turbulenceScale: 3,
            drift: 0.35,
            seed: context.seed % 9999,
          },
        };
      }
      return {
        patternId: 'waves',
        params: {
          profile: context.sharpness >= 0.5 ? 'triangle' : 'sine',
          wavelength: span(context, 1),
          angle: context.angle,
          meander: context.words.has('wavy') ? 0.3 : 0,
          meanderSpan: 2,
        },
      };
    },
  },
  {
    id: 'dunes',
    strong: ['dune', 'dunes', 'windswept'],
    keywords: ['sand', 'desert', 'drift'],
    depth: 11,
    build: (context) => ({
      patternId: 'dunes',
      params: {
        profile: 'round',
        wavelength: span(context, 1.25),
        turbulence: 0.7,
        turbulenceScale: 3,
        drift: 0.5,
        seed: context.seed % 9999,
      },
    }),
  },
  {
    id: 'pyramids',
    strong: ['pyramid', 'pyramids'],
    keywords: ['spike', 'spikes', 'cone', 'cones', 'peak', 'peaks', 'studs', 'tips'],
    depth: 12,
    build: (context) => ({
      patternId: 'pyramids',
      params: {
        shape: context.words.has('cone') || context.words.has('cones') ? 'cone' : 'pyramid',
        cell: span(context, 0.5),
        sharpness: context.sharpness >= 0 ? 1.2 : 0.7,
        variation: context.irregular ? 0.4 : 0,
        seed: context.seed % 9999,
      },
    }),
  },
  {
    id: 'diamonds',
    strong: ['diamond', 'diamonds', 'quilt', 'quilted'],
    keywords: ['rhombus', 'lozenge', 'upholstery'],
    depth: 8,
    build: (context) => ({
      patternId: 'pyramids',
      params: {
        shape: 'diamond',
        cell: span(context, 0.5),
        stagger: 0.5,
        sharpness: context.sharpness >= 0 ? 1 : 0.65,
      },
    }),
  },
  {
    id: 'ribs',
    strong: ['rib', 'ribs', 'flute', 'fluted', 'fluting', 'slat', 'slats', 'reeded', 'batten'],
    keywords: ['stripe', 'stripes', 'groove', 'grooves', 'acoustic', 'line', 'lines'],
    depth: 12,
    build: (context) => ({
      patternId: 'ribs',
      params: {
        spacing: span(context, 0.25),
        width: 0.65,
        angle: context.angle === 0 ? 90 : context.angle,
        reliefShape: context.sharpness >= 0.5 ? 'flat' : 'round',
        bevel: 0.15,
      },
    }),
  },
  {
    id: 'bricks',
    strong: ['brick', 'bricks', 'masonry', 'ashlar'],
    keywords: ['stone', 'block', 'blocks', 'tile', 'tiles', 'wall', 'stacked'],
    depth: 9,
    build: (context) => ({
      patternId: 'bricks',
      params: {
        length: span(context, 1),
        height: span(context, 0.25),
        stagger: 0.5,
        joint: 0.07,
        bevel: 0.16,
        variation: context.irregular ? 0.4 : 0.1,
        seed: context.seed % 9999,
      },
    }),
  },
  {
    id: 'domes',
    strong: ['bubble', 'bubbles', 'dome', 'domes', 'polka'],
    keywords: ['dot', 'dots', 'sphere', 'spheres', 'ball', 'balls', 'blob', 'blobs'],
    depth: 10,
    build: (context) => ({
      patternId: 'domes',
      params: {
        cell: span(context, 0.375),
        stagger: 0.5,
        coverage: 1,
        variation: context.irregular ? 0.5 : 0,
        seed: context.seed % 9999,
      },
    }),
  },
  {
    id: 'weave',
    strong: ['weave', 'woven', 'basket', 'rattan', 'wicker'],
    keywords: ['textile', 'fabric', 'braid', 'knit'],
    depth: 7,
    build: (context) => ({
      patternId: 'weave',
      params: { strand: span(context, 0.25), depth: 0.45, gap: 0.06 },
    }),
  },
  {
    id: 'facets',
    strong: ['facet', 'facets', 'faceted', 'low-poly', 'lowpoly', 'origami'],
    keywords: ['polygon', 'crystal', 'crystals', 'gem', 'shard', 'shards'],
    depth: 11,
    build: (context) => ({
      patternId: 'facets',
      params: {
        cell: span(context, 0.5),
        relief: 0.9,
        terrace: context.terraced ? 0.6 : 0,
        seed: context.seed % 9999,
      },
    }),
  },
  {
    id: 'cells',
    strong: ['voronoi', 'crack', 'cracks', 'cracked', 'honeycomb', 'pebble', 'pebbles'],
    keywords: ['cell', 'cells', 'coral', 'leaf', 'scales', 'reptile', 'lichen'],
    depth: 9,
    build: (context): MotifResult => {
      const cracked =
        context.words.has('crack') || context.words.has('cracks') || context.words.has('cracked');
      const bordered = context.words.has('honeycomb') || context.words.has('scales');
      return {
        patternId: 'cells',
        params: {
          cell: span(context, 0.3),
          mode: cracked ? 'crackle' : bordered ? 'cells' : 'pebbles',
          sharpness: context.sharpness >= 0 ? 1.3 : 0.8,
          seed: context.seed % 9999,
        },
      };
    },
  },
  {
    id: 'flow',
    strong: ['topographic', 'topography', 'contour', 'contours', 'strata', 'stratified'],
    keywords: ['sediment', 'canyon', 'map', 'geological', 'marble'],
    depth: 8,
    build: (context) => ({
      patternId: 'flow',
      params: {
        scale: span(context, 2.5),
        strength: context.words.has('marble') ? 5 : 3,
        bands: Math.round(clamp(10 / context.scale, 3, 30)),
        angle: context.angle,
        seed: context.seed % 9999,
      },
    }),
  },
  {
    id: 'organic',
    strong: ['wood', 'grain', 'bark'],
    keywords: [
      'organic',
      'cloud',
      'clouds',
      'smoke',
      'liquid',
      'lava',
      'terrain',
      'hills',
      'mountain',
      'landscape',
      'flow',
      'melt',
    ],
    depth: 8,
    build: (context) => ({
      patternId: 'organic',
      params: {
        scale: span(context, 1.5),
        octaves: context.words.has('wood') || context.words.has('grain') ? 3 : 5,
        gain: 0.5,
        ridged: context.sharpness >= 0.5,
        warp: context.words.has('wood') || context.words.has('grain') ? 0.8 : 0.45,
        warpScale: 3,
        seed: context.seed % 9999,
      },
    }),
  },
];

/** Applies the prompt's coarse/fine modifier to a base span, in panels. */
function span(context: PromptContext, base: number): number {
  if (context.span !== null) return clamp(context.span, 0.0625, 12);
  return clamp(Math.round((base * context.scale) / 0.0625) * 0.0625, 0.0625, 12);
}

function countMatches(words: Set<string>, keywords: readonly string[]): number {
  let matches = 0;
  for (const keyword of keywords) {
    if (words.has(keyword)) matches += 1;
  }
  return matches;
}

function hasAny(words: Set<string>, keywords: readonly string[]): boolean {
  return countMatches(words, keywords) > 0;
}

export function analysePrompt(prompt: string, seedOverride?: number): PromptContext {
  const normalised = prompt.toLowerCase();
  const words = new Set(normalised.split(/[^a-z0-9-]+/).filter(Boolean));
  const notes: string[] = [];

  let scale = 1;
  if (hasAny(words, MODIFIERS.finer)) {
    scale *= 0.5;
    notes.push('Finer features requested — feature size halved.');
  }
  if (hasAny(words, MODIFIERS.coarser)) {
    scale *= 2;
    notes.push('Coarser features requested — feature size doubled.');
  }

  let depth = 1;
  if (hasAny(words, MODIFIERS.deeper)) {
    depth *= 1.5;
    notes.push('Deeper relief requested.');
  }
  if (hasAny(words, MODIFIERS.shallower)) {
    depth *= 0.55;
    notes.push('Shallower relief requested.');
  }

  let sharpness = 0;
  if (hasAny(words, MODIFIERS.sharper)) sharpness += 1;
  if (hasAny(words, MODIFIERS.softer)) sharpness -= 1;

  let angle = 0;
  if (hasAny(words, MODIFIERS.vertical)) {
    angle = 90;
    notes.push('Oriented vertically.');
  } else if (hasAny(words, MODIFIERS.diagonal)) {
    angle = 45;
    notes.push('Oriented diagonally.');
  } else if (hasAny(words, MODIFIERS.horizontal)) {
    angle = 0;
  }

  // "spanning three panels", "across 2 panels", "4-panel motif"
  const spanMatch = normalised.match(/(\d+(?:\.\d+)?)\s*(?:-|\s)?panels?/);
  const span = spanMatch ? Number(spanMatch[1]) : null;
  if (span !== null && Number.isFinite(span)) {
    notes.push(`Motif sized to span ${span} panel${span === 1 ? '' : 's'}.`);
  }

  const irregular = hasAny(words, MODIFIERS.irregular);
  const terraced = hasAny(words, MODIFIERS.terraced);
  const faded = hasAny(words, MODIFIERS.faded);

  return {
    prompt,
    words,
    seed: seedOverride ?? hashString(normalised),
    scale,
    depth,
    sharpness: clamp(sharpness, -1, 1),
    angle,
    span: span !== null && Number.isFinite(span) ? span : null,
    irregular,
    terraced,
    faded,
    notes,
  };
}

/** Strong words count double; supporting words count once. */
function ruleScore(context: PromptContext, rule: MotifRule): number {
  return countMatches(context.words, rule.strong) * 2 + countMatches(context.words, rule.keywords);
}

function pickMotif(context: PromptContext): MotifRule {
  let best: MotifRule | null = null;
  let bestScore = 0;

  for (const rule of MOTIF_RULES) {
    const score = ruleScore(context, rule);
    if (score > bestScore) {
      best = rule;
      bestScore = score;
    }
  }

  if (best) return best;

  // Nothing recognised: an organic field seeded from the prompt still gives
  // something usable and, importantly, something different for each prompt.
  context.notes.push(
    'No known motif in the prompt — generated an organic relief seeded from the text.',
  );
  return MOTIF_RULES[MOTIF_RULES.length - 1];
}

/** Builds a layer stack and a finish from a prompt. Pure and deterministic. */
export function synthesiseFromPrompt(prompt: string, seed?: number): TextureGenerationResult {
  const context = analysePrompt(prompt, seed);
  const motif = pickMotif(context);
  const base = motif.build(context);

  const layers: PatternLayer[] = [createLayer(base.patternId, { params: base.params })];

  // A second noise layer is what separates "machined" from "made": it breaks up
  // the regularity without destroying the motif underneath.
  if (context.irregular && base.patternId !== 'organic' && base.patternId !== 'cells') {
    layers.push(
      createLayer('organic', {
        params: {
          scale: 0.75,
          octaves: 4,
          gain: 0.55,
          warp: 0.4,
          warpScale: 2,
          seed: (context.seed >> 3) % 9999,
        },
        blend: 'overlay',
        amount: 0.35,
      }),
    );
    context.notes.push('Added an organic overlay to break up the regularity.');
  }

  if (context.faded) {
    layers.push(
      createLayer('gradient', {
        params: { mode: 'linear', angle: context.angle + 90, start: 0.1, end: 1, ease: true },
        blend: 'multiply',
      }),
    );
    context.notes.push('Added a gradient so the relief fades across the wall.');
  }

  const finish: Partial<ReliefFinish> = {
    contrast: clamp01(context.sharpness > 0 ? 0.35 : 0.1),
    gamma: context.sharpness < 0 ? 1.2 : 1,
    terraceSteps: context.terraced ? 7 : 0,
  };
  if (context.terraced) context.notes.push('Terraced the relief into discrete steps.');

  return {
    label: prompt.trim().slice(0, 60) || 'Generated texture',
    layers,
    finish,
    suggestedDepth: clamp(motif.depth * context.depth, 1, 40),
    notes: context.notes,
  };
}

/** Exposed so the UI can offer example prompts that actually resolve well. */
export const EXAMPLE_PROMPTS: readonly string[] = [
  'deep ocean waves spanning 3 panels',
  'fine vertical acoustic slats',
  'cracked desert earth, irregular',
  'sharp low-poly crystal facets',
  'soft quilted diamonds',
  'topographic contour map, terraced',
  'stacked stone wall, weathered',
  'large bubbles fading across the wall',
];
